import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { supabase } from "@/integrations/supabase/client"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Wallet, Loader2, Mail, Lock, User, Moon, Sun, CheckCircle2, XCircle } from "lucide-react"
import { toast } from "sonner"
import { useTheme } from "next-themes"
import { validatePassword, getPasswordStrengthColor, getPasswordRequirements, type PasswordValidationResult } from "@/lib/utils/password-validation"

export default function AuthPage() {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const { theme, setTheme } = useTheme()

  // Login state
  const [loginEmail, setLoginEmail] = useState("")
  const [loginPassword, setLoginPassword] = useState("")

  // Signup state
  const [signupEmail, setSignupEmail] = useState("")
  const [signupPassword, setSignupPassword] = useState("")
  const [signupFirstName, setSignupFirstName] = useState("")
  const [signupLastName, setSignupLastName] = useState("")
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidationResult | null>(null)
  
  // Validate password in real-time
  useEffect(() => {
    if (signupPassword) {
      const validation = validatePassword(signupPassword)
      setPasswordValidation(validation)
    } else {
      setPasswordValidation(null)
    }
  }, [signupPassword])

  // Diagnostic logging - verify Lovable Cloud configuration
  useEffect(() => {
    console.log('🔍 Supabase Client Configuration:', {
      url: import.meta.env.VITE_SUPABASE_URL,
      projectId: import.meta.env.VITE_SUPABASE_PROJECT_ID,
      keyPrefix: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY?.substring(0, 20) + '...',
      expectedProjectId: 'gjhujfatwxskqcvxxfdo'
    })
  }, [])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      console.log("🔐 Attempting login...")
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email: loginEmail,
        password: loginPassword,
      })

      if (error) {
        console.error("❌ Login error:", error)
        toast.error(error.message)
        return
      }

      console.log("✅ Login successful:", { userId: data.user?.id })
      toast.success("Welcome back!")
      navigate("/home")
    } catch (error) {
      console.error("❌ Unexpected login error:", error)
      toast.error("An unexpected error occurred")
    } finally {
      setIsLoading(false)
    }
  }

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate names
    if (!signupFirstName.trim()) {
      toast.error("Please enter your first name")
      return
    }
    if (!signupLastName.trim()) {
      toast.error("Please enter your last name")
      return
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(signupEmail)) {
      toast.error("Please enter a valid email address")
      return
    }

    // Strong password validation
    const validation = validatePassword(signupPassword)

    if (!validation.isValid) {
      toast.error(validation.errors[0]) // Show first error
      return
    }

    setIsLoading(true)

    try {
      console.log("📝 Attempting signup for:", signupEmail)
      console.log('🌐 Backend URL for signup request:', {
        supabaseUrl: (supabase as any).supabaseUrl || 'URL not accessible',
        restUrl: (supabase as any).restUrl || 'REST URL not accessible',
      })
      
      // Sign up the user with emailRedirectTo
      const { data: authData, error: signupError } = await supabase.auth.signUp({
        email: signupEmail,
        password: signupPassword,
        options: {
          emailRedirectTo: `${window.location.origin}/home`,
          data: {
            first_name: signupFirstName,
            last_name: signupLastName,
          },
        },
      })

      // Log full error for debugging
      if (signupError) {
        console.error("❌ Signup error:", {
          message: signupError.message,
          status: signupError.status,
          code: signupError.name,
          fullError: signupError
        })
        
        // Check for specific error codes
        if (signupError.message.includes("already registered") || signupError.message.includes("already exists")) {
          toast.error("This email is already registered. Please try logging in instead.")
        } else {
          toast.error(signupError.message)
        }
        return
      }

      if (!authData.user) {
        console.error("❌ No user data returned from signup")
        toast.error("Failed to create account")
        return
      }

      console.log("✅ Signup successful:", { 
        userId: authData.user.id, 
        email: authData.user.email,
        session: authData.session ? "created" : "not created"
      })
      
      toast.success("Account created successfully!")
      navigate("/home")
    } catch (error) {
      console.error("❌ Unexpected signup error:", error)
      toast.error("An unexpected error occurred")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50/30 via-white to-green-50/20 dark:from-slate-950 dark:via-slate-900 dark:to-slate-900 p-4">
      {/* Theme Toggle */}
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-4 right-4 rounded-full"
        onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
      >
        {theme === "dark" ? (
          <Sun className="h-5 w-5" />
        ) : (
          <Moon className="h-5 w-5" />
        )}
        <span className="sr-only">Toggle theme</span>
      </Button>

      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="mb-4 inline-block">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-[var(--brand-amber)] opacity-20 blur-xl animate-pulse" />
              <picture>
                <source srcSet="/logo.avif" type="image/avif" />
                <source srcSet="/logo.webp" type="image/webp" />
                <img
                  src="/logo.webp"
                  alt="Money Bees Logo"
                  width="96"
                  height="96"
                  fetchPriority="high"
                  decoding="async"
                  className="relative w-24 h-24 mx-auto rounded-full shadow-md ring-2 ring-[var(--brand-amber)]/30"
                />
              </picture>
            </div>
          </div>
          <h1 className="text-3xl font-bold mb-2">Money Bees</h1>
          <p className="text-muted-foreground">Manage your finances with ease</p>
        </div>

        <Tabs defaultValue="login" className="w-full min-h-[480px]">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger
              value="login"
              className="data-[state=active]:border-b-2 data-[state=active]:border-[var(--brand-green)] data-[state=active]:text-foreground data-[state=active]:bg-[var(--brand-green)]/20 dark:data-[state=active]:bg-[var(--brand-green)]/15 transition-all duration-200"
            >
              Login
            </TabsTrigger>
            <TabsTrigger
              value="signup"
              className="data-[state=active]:border-b-2 data-[state=active]:border-[var(--brand-green)] data-[state=active]:text-foreground data-[state=active]:bg-[var(--brand-green)]/20 dark:data-[state=active]:bg-[var(--brand-green)]/15 transition-all duration-200"
            >
              Sign Up
            </TabsTrigger>
          </TabsList>

          {/* Login Tab */}
          <TabsContent
            value="login"
            className="data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:slide-in-from-bottom-4 duration-300"
          >
            <Card>
              <CardHeader>
                <CardTitle>Welcome back</CardTitle>
                <CardDescription>Enter your credentials to access your account</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="you@example.com"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        className="pl-10 focus-visible:ring-[var(--brand-amber)]/50 focus-visible:border-[var(--brand-amber)]"
                        required
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        className="pl-10 focus-visible:ring-[var(--brand-amber)]/50 focus-visible:border-[var(--brand-amber)]"
                        required
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full hover:shadow-[0_0_20px_rgba(var(--brand-amber-light),0.3)] transition-shadow duration-300"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      "Sign In"
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Signup Tab */}
          <TabsContent
            value="signup"
            className="data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:slide-in-from-bottom-4 duration-300"
          >
            <Card>
              <CardHeader>
                <CardTitle>Create your account</CardTitle>
                <CardDescription>Start managing your finances today</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSignup} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="signup-first-name">First Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signup-first-name"
                          type="text"
                          placeholder="John"
                          value={signupFirstName}
                          onChange={(e) => setSignupFirstName(e.target.value)}
                          className="pl-10 focus-visible:ring-[var(--brand-amber)]/50 focus-visible:border-[var(--brand-amber)]"
                          required
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-last-name">Last Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="signup-last-name"
                          type="text"
                          placeholder="Doe"
                          value={signupLastName}
                          onChange={(e) => setSignupLastName(e.target.value)}
                          className="pl-10 focus-visible:ring-[var(--brand-amber)]/50 focus-visible:border-[var(--brand-amber)]"
                          required
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="signup-email"
                        type="email"
                        placeholder="you@example.com"
                        value={signupEmail}
                        onChange={(e) => setSignupEmail(e.target.value)}
                        className="pl-10 focus-visible:ring-[var(--brand-amber)]/50 focus-visible:border-[var(--brand-amber)]"
                        required
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="signup-password"
                        type="password"
                        placeholder="••••••••••••"
                        value={signupPassword}
                        onChange={(e) => setSignupPassword(e.target.value)}
                        className="pl-10 focus-visible:ring-[var(--brand-amber)]/50 focus-visible:border-[var(--brand-amber)]"
                        required
                        minLength={12}
                        disabled={isLoading}
                      />
                    </div>
                    
                    {/* Password Strength Indicator */}
                    {passwordValidation && signupPassword && (
                      <div className="mt-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Password strength:</span>
                          <span className={`text-sm font-medium ${getPasswordStrengthColor(passwordValidation.strength)}`}>
                            {passwordValidation.strength.toUpperCase()}
                          </span>
                        </div>
                        
                        {/* Password Requirements Checklist */}
                        <div className="space-y-1 text-xs">
                          {getPasswordRequirements().map((req, idx) => {
                            const checks = [
                              { pattern: /12 characters/, test: signupPassword.length >= 12 },
                              { pattern: /uppercase/, test: /[A-Z]/.test(signupPassword) },
                              { pattern: /lowercase/, test: /[a-z]/.test(signupPassword) },
                              { pattern: /number/, test: /[0-9]/.test(signupPassword) },
                              { pattern: /special/, test: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(signupPassword) },
                            ]
                            const isMet = checks[idx]?.test || false
                            return (
                              <div key={idx} className={`flex items-center gap-2 ${isMet ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`}>
                                {isMet ? (
                                  <CheckCircle2 className="h-3 w-3" />
                                ) : (
                                  <XCircle className="h-3 w-3" />
                                )}
                                <span>{req}</span>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* Show requirements when no password entered */}
                    {!signupPassword && (
                      <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                        <p className="font-medium">Password must include:</p>
                        {getPasswordRequirements().map((req, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <XCircle className="h-3 w-3" />
                            <span>{req}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <Button
                    type="submit"
                    className="w-full hover:shadow-[0_0_20px_rgba(var(--brand-amber-light),0.3)] transition-shadow duration-300"
                    disabled={isLoading || (passwordValidation && !passwordValidation.isValid)}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating account...
                      </>
                    ) : (
                      "Create Account"
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Footer */}
        <p className="text-center text-sm text-muted-foreground mt-6">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  )
}
